import { create } from "zustand";
import { persist } from "zustand/middleware";

interface ProductMedia {
  id: string;
  url: string;
  type: string;
  order: number;
  productId: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  condition: string;
  categoryId: string;
  sellerId: number;
  status: string;
  views: number;
  createdAt: Date;
  updatedAt: Date;
  media: ProductMedia[];
  shipping: ShippingOption[];
  reviews: Review[];
  orders: Order[];
}

interface ShippingOption {
  id: string;
  name: string;
  price: number;
  estimatedDays: number;
  productId: string;
}

interface Review {
  id: string;
  rating: number;
  comment?: string;
  productId: string;
  orderId: string;
  reviewerId: number;
}

interface Order {
  id: string;
  buyerId: number;
  productId: string;
  quantity: number;
  status: string;
  shippingAddress: string;
  shippingOptionId: string;
}

export interface CartItem {
  productId: string;
  quantity: number;
  product: Product;
  selectedShippingId?: string;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  addItem: (product: Product) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateShipping: (productId: string, shippingId: string) => void;
  clearCart: () => void;
  itemCount: () => number;
  getItemCount: () => number;
  total: () => number;
  shippingTotal: () => number;
  grandTotal: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      setIsOpen: (isOpen) => set({ isOpen }),
      addItem: (product) => {
        const items = get().items;
        const existingItem = items.find((item) => item.productId === product.id);

        if (existingItem) {
          set({
            items: Array.isArray(items) ? items.map((item) =>
              item.productId === product.id
                ? { ...item, quantity: item.quantity + 1 }
                : item
            ) : [],
          });
        } else {
          set({
            items: [
              ...items,
              {
                productId: product.id,
                quantity: 1,
                product,
              },
            ],
          });
        }
      },
      removeItem: (productId) =>
        set((state) => ({
          items: state?.items?.filter((item) => item.productId !== productId),
        })),
      updateQuantity: (productId, quantity) =>
        set((state) => ({
          items: Array.isArray(state.items) ? state.items.map((item) =>
            item.productId === productId ? { ...item, quantity } : item
          ) : [],
        })),
      updateShipping: (productId, shippingId) =>
        set((state) => ({
          items: Array.isArray(state.items) ? state.items.map((item) =>
            item.productId === productId
              ? { ...item, selectedShippingId: shippingId }
              : item
          ) : [],
        })),
      clearCart: () => set({ items: [] }),
      itemCount: () => {
        const items = get().items;
        return items.reduce((total, item) => total + item.quantity, 0);
      },
      getItemCount: () => {
        const items = get().items;
        return items.reduce((total, item) => total + item.quantity, 0);
      },
      total: () => {
        const items = get().items;
        return items.reduce(
          (total, item) => total + item?.product?.price * item.quantity,
          0
        );
      },
      shippingTotal: () => {
        const items = get().items;
        return items.reduce((total, item) => {
          if (!item.selectedShippingId) return total;
          const shippingOption = item?.product?.shipping.find(
            (option) => option.id === item.selectedShippingId
          );
          return total + (shippingOption?.price || 0);
        }, 0);
      },
      grandTotal: () => {
        const store = get();
        return store.total() + store.shippingTotal();
      },
    }),
    {
      name: "cart-storage",
    }
  )
);